import axios from "axios";
import ProgressSmoother, {ProgressSmootherConfig} from "@/utils/ProgressSmoother";
import {ExtractionJob, JobDetails, MediaMetaData, PersistedExtractionJob} from "@/types";
import {ExtractionMethod, ExtractionJobStatus} from "@/types/enums";
import InsufficientCreditsException from "@/exceptions/InsufficientCreditsException";
import { createProgressListener, ProgressListener } from "@/utils/progressListener";
import BatchJobSubmissionException from "@/exceptions/BatchJobSubmissionException";

export type JobProgressListener = ProgressListener;

const JOB_STARTUP_SECONDS = 100;
const JOB_STARTUP_PERCENT_PROGRESS = 70;
const BASELINE_VIDEO_DURATION = 60;
const AVG_VALUE_INCREASE_DELTA = 10;

const smoothingConfigOcr: ProgressSmootherConfig = {
    maxValue: 100,
    averageTimeBetweenValues: 20_000,
    averageValueIncreaseDelta: AVG_VALUE_INCREASE_DELTA,
    delayUntilFirstValue: JOB_STARTUP_SECONDS * 1000,
    firstValue: JOB_STARTUP_PERCENT_PROGRESS,
}

const smoothingConfigAudio: ProgressSmootherConfig = {
    maxValue: 100,
    averageTimeBetweenValues: 10_000,
    averageValueIncreaseDelta: 10,
    delayUntilFirstValue: 125 * 1000,
    firstValue: 70,
}

export type ListenerConfig = {
    on_update: (progress: number) => void,
    on_error: (error: Error) => void
    on_complete?: () => void,
    job_name: string,
    video_duration: number,
    updates_per_second?: number,
    started_at?: number,
    method: ExtractionMethod
}

export function isJobRunning(status: ExtractionJobStatus) {
    return status === ExtractionJobStatus.Queued ||
        status === ExtractionJobStatus.Running ||
        status === ExtractionJobStatus.Starting ||
        status === ExtractionJobStatus.Submitted;
}

export function isJobStarting(status: ExtractionJobStatus) {
    return [ExtractionJobStatus.Queued, ExtractionJobStatus.Submitted].includes(status);
}

export function listenForExtractionJobUpdates({
    on_update,
    on_error,
    on_complete,
    job_name,
    method,
    video_duration,
    started_at,
    updates_per_second = 2,
}: ListenerConfig): JobProgressListener {

    const smoothingConfig = makeSmoothingConfig(video_duration, method);
    const progressSmoother = new ProgressSmoother({...smoothingConfig, started_at});

    return createProgressListener({
        jobId: job_name,
        progressSmoother,
        onComplete: on_complete,
        updatesPerSecond: updates_per_second,
        waitForRunning: true,
        requestInitialProgress: true,
        onProgress: on_update,
        onError: on_error,
    });
}

function makeSmoothingConfig(videoDuration: number, method: ExtractionMethod): ProgressSmootherConfig {
    const template = method == "ocr" ? smoothingConfigOcr : smoothingConfigAudio;
    let config = {...template};
    if (!videoDuration) {
        return config;
    }
    const durationMinutes = videoDuration / 60;
    const durationFactor = 1 / (durationMinutes / BASELINE_VIDEO_DURATION);
    config.averageValueIncreaseDelta = Math.min(durationFactor, 1) * config.averageValueIncreaseDelta;
    config.firstValue = Math.min(durationFactor, 1) * config.firstValue;
    return config;
}

export async function cancelJob(job: ExtractionJob) {
    try {
        return await axios.put(route('job.cancel', job.id));
    } catch (err: any) {
        throw new Error(err.response?.data?.message || err.message);
    }
}

export function deleteJob(job: ExtractionJob) {
    return axios.delete(route('job.delete', job.id)).catch(err => {
        console.error(err)
    })
}

export async function getMediaMetadata(uploadId: string): Promise<MediaMetaData> {
    try {
        const res = await axios.get(route('upload.metadata', {id: uploadId}))
        return res.data as MediaMetaData;
    } catch (e: any) {
        const errorMessage = e.response?.data?.message || e.message
        throw new Error(errorMessage)
    }
}

export async function retryJob(job: PersistedExtractionJob): Promise<void> {
    try {
        await axios.post(route('job.retry', job.id));
    } catch (e: any) {
        throw new Error(e.response?.data?.message || 'An error occurred while retrying the job');
    }
}

/**
 * Creates a copy of the upload file associated with a job, extending its availability for another 24 hours.
 * This allows users to retry extraction with different settings without re-uploading the original file.
 */
export async function duplicateUpload(job: ExtractionJob): Promise<string> {
    try {
        const response = await axios.post(route('job.duplicate', job.id));
        return response.data.uploadId as string;
    } catch (e: any) {
        throw new Error(e.response?.data?.message || e.message);
    }
}

/**
 * Starts a batch of extraction jobs.
 *
 * @throws InsufficientCreditsException if the user has insufficient credits to start the job.
 * @throws BatchJobSubmissionException if there are errors in the batch job submission.
 */
export async function startJobs(details: JobDetails[]): Promise<number> {
    try {
        const uploads = details.map(function(job) {
            return {
                id: job.id,
                fileName: job.fileName,
                methodParams: job.extractionParams || {},
                metadata: JSON.stringify(job.metaData),
                folderId: job.folderId
            }
        });
        const method = details[0].method;
        const language = details[0].language;
        const workflowId = details[0].workflowId;
        const payload = { method, language, uploads, workflowId }
        const response = await axios.post(route('job.start'), payload);
        return response.data.creditBalance;
    } catch (err: any) {
        if (err.response) {
            const data = err.response?.data || {};
            if (data.type === "insufficient_credits") {
                throw (new InsufficientCreditsException(data.balance));
            } else if(data.type === "BatchJobSubmissionException") {
                throw new BatchJobSubmissionException(data);
            } else {
                const msg = data.message ? data.message : JSON.stringify(data);
                throw new Error(msg);
            }
        } else {
            throw err;
        }
    }
}

/**
 * Warm up GPU capacity for upcoming extraction job.
 * Should be called for audio extraction jobs that will use GPU processing.
 */
export async function reserveGpuCapacity(uploadIds: string[], timeoutMs = 10_000): Promise<{ success: boolean, expires_seconds: number}> {
    try {
        const response = await axios.post(
            route('capacity.reserve'),
            { uploads: uploadIds },
            { timeout: timeoutMs }
        );
        return response.data;
    } catch (e) {
        console.error('GPU warmup failed:', e);
        return { success: false, expires_seconds: 0 };
    }
}

/**
 * Cancels GPU warmup for a list of uploads.
 * Uses fetch() with keepalive instead of axios to ensure the request
 * completes even if the page is being unloaded (e.g., beforeunload event).
 * @param uploadIds
 * @param reason
 */
export async function cancelGpuCapacity(uploadIds: string[], reason: string): Promise<void> {
    try {
        await fetch(route('capacity.cancel', { reason }), {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': window.csrfToken(),
            },
            body: JSON.stringify({ uploads: uploadIds }),
            keepalive: true
        });
    } catch (e) {
        console.error('Failed to cancel GPU warmup:', e);
    }
}

export async function getUploadUrl(jobId: number): Promise<string> {
    try {
        const res = await axios.get(route('subtitle.uploadUrl', jobId));
        return res.data.url;
    } catch (e: any) {
        throw new Error(e.response?.data?.message || 'Failed to get upload URL');
    }
}
